import { EMPLOYEE_PARTS, NUMERIC_FIELDS, hasValue, sameId } from "./employeeModel";

const EXCLUDED_EMPLOYEE_FIELDS = new Set([
  "errors",
  "showAllSpecXFunc",
  "persistenceUncertain",
  "decryptionFailed",
  "employeeAbsences",
]);

const normalizeEmployeePersistenceValues = (employee = {}) =>
  Object.entries(employee).reduce((result, [field, value]) => {
    if (!field || EXCLUDED_EMPLOYEE_FIELDS.has(field)) return result;
    if (value === null || value === undefined) return { ...result, [field]: value };
    if (typeof value !== "string") return { ...result, [field]: value };
    const trimmed = value.trim();
    if (!NUMERIC_FIELDS.has(field) || trimmed === "") return { ...result, [field]: trimmed };
    const number = Number(trimmed);
    return { ...result, [field]: Number.isFinite(number) ? number : trimmed };
  }, {});

export async function prepareEmployeeForPersistence(employee, engine) {
  if (!engine?.encryptEmployee) throw new Error("Employee persistence requires explicit crypto capabilities");
  const normalized = normalizeEmployeePersistenceValues(employee);
  const nameOrNum = String(normalized.nameOrNum ?? "").trim();
  if (!nameOrNum) throw new Error("Employee name or number is required.");
  return {
    ...normalized,
    hash: normalized.hash || (await engine.encryptEmployee(nameOrNum)),
    errors: {},
  };
}

export async function prepareEmployeeForUpsert({ employeeInput, existingEmployees = [], engine }) {
  if (employeeInput?.persistenceUncertain) {
    const error = new Error("The previous employee save could not be rolled back. Reload the survey before retrying.");
    error.requiresReload = true;
    throw error;
  }
  const employee = await prepareEmployeeForPersistence(employeeInput, engine);
  const existingEmployee =
    existingEmployees.find(
      (candidate) =>
        sameId(candidate.employeeId, employee.employeeId) ||
        sameId(candidate.id, employee.id) ||
        sameId(candidate.employeeId, employee.id)
    ) || null;
  return {
    employee,
    existingEmployee,
    canUpdate: hasValue(employee.employeeId) || Boolean(existingEmployee),
  };
}

export function clearEmployeePersistedIds(employee) {
  const result = { ...employee };
  EMPLOYEE_PARTS.forEach(({ recordIdKey }) => delete result[recordIdKey]);
  delete result.employeeAbsencesId;
  delete result.id;
  delete result.persistenceUncertain;
  return result;
}
